import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './OnJumprun.css';

const HERO_IMG = 'https://images.unsplash.com/photo-1601024445121-e5b82f020549?w=1600&q=80';
const SHOT1 = 'https://images.unsplash.com/photo-1583521214690-73421a1829a9?w=900&q=80';
const SHOT2 = 'https://images.unsplash.com/photo-1540979388789-6cee28a1cdc9?w=900&q=80';

const FEATURES = [
    {
        icon: '📋',
        title: 'Live Manifest Board',
        body: 'Every load, every jumper, every status — updated in real time. Fun jumpers see the board from their phone. Staff see it on the wall. No clipboards.',
    },
    {
        icon: '✈️',
        title: 'Load Management',
        body: 'Create loads, assign tandem slots to instructors, and move jumpers through the full lifecycle: boarding → in the air → landed.',
    },
    {
        icon: '🪂',
        title: 'Tandem Reservations',
        body: 'Schedule tandems against configurable hourly slot limits. Each instructor holds one slot per load — the system enforces it automatically.',
    },
    {
        icon: '👤',
        title: 'Fun Jumper Self-Service',
        body: 'Licensed jumpers log in, see the live manifest, and request to be added to a load — without calling the office or walking up to the desk.',
    },
];

const COMPARE = [
    ['Live manifest board', true, true],
    ['Load status tracking (boarding / air / landed)', true, false],
    ['Fun jumper self-service requests', true, false],
    ['Tandem slot limits per instructor per load', true, false],
    ['Configurable tandems-per-hour cap', true, false],
    ['Modern mobile-friendly UI', true, false],
    ['Tandem reservation management', true, true],
];

const OnJumprun = () => {
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        document.documentElement.classList.add('sub-page');
        document.title = 'OnJumprun — Drop Zone Management Software';
        return () => {
            document.documentElement.classList.remove('sub-page');
            document.title = 'Roland Forbes | Software Engineer';
        };
    }, []);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    return (
        <div className="oj-page">

            {/* ── Nav ── */}
            <nav className={`oj-nav${scrolled ? ' oj-nav--scrolled' : ''}`}>
                <div className="oj-nav-inner">
                    <div className="oj-logo">
                        <span className="oj-logo-on">On</span><span className="oj-logo-jump">Jumprun</span>
                    </div>
                    <div className="oj-nav-links">
                        <a href="#features">Features</a>
                        <a href="#compare">Why Us</a>
                        <a href="#contact">Contact</a>
                    </div>
                    <div className="oj-nav-right">
                        <a href="#contact" className="oj-btn-nav">Request Demo</a>
                        <Link to="/" className="oj-back-link">← Portfolio</Link>
                    </div>
                </div>
            </nav>

            {/* ── Hero ── */}
            <section className="oj-hero">
                <div className="oj-hero-bg" style={{ backgroundImage: `url(${HERO_IMG})` }} />
                <div className="oj-hero-content">
                    <span className="oj-hero-tag">Built for Drop Zone Operators</span>
                    <h1 className="oj-hero-h1">Manifest your DZ.<br />In real time.</h1>
                    <p className="oj-hero-sub">
                        OnJumprun, software built for DZOs. Manage loads, tandem reservations,
                        and fun jumper slots — with a live board your whole dropzone can see from any device.
                    </p>
                    <div className="oj-hero-btns">
                        <a href="#contact" className="oj-btn-primary">Request a Demo</a>
                        <a href="#features" className="oj-btn-ghost">See Features</a>
                    </div>
                </div>
            </section>

            {/* ── Features ── */}
            <section className="oj-features" id="features">
                <div className="oj-container">
                    <p className="oj-eyebrow">Everything in one platform</p>
                    <h2 className="oj-section-h2">From the reservation<br />to the landing.</h2>
                    <div className="oj-features-grid">
                        {FEATURES.map(f => (
                            <div className="oj-feature-card" key={f.title}>
                                <span className="oj-feature-icon">{f.icon}</span>
                                <h3 className="oj-feature-title">{f.title}</h3>
                                <p className="oj-feature-body">{f.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Spotlight 1 ── */}
            <section className="oj-spotlight oj-spotlight--dark">
                <div className="oj-container oj-spotlight-inner">
                    <div className="oj-spotlight-text">
                        <p className="oj-eyebrow oj-eyebrow--light">Live Manifest</p>
                        <h2 className="oj-spotlight-h2">Everyone knows where they stand.</h2>
                        <p className="oj-spotlight-body">
                            The manifest board updates in real time as loads are built, filled, and called.
                            Fun jumpers check their spot from their phone. Staff see load status at a glance —
                            boarding, in the air, or landed. No one has to ask.
                        </p>
                        <a href="#contact" className="oj-btn-primary">See It in Action</a>
                    </div>
                    <div className="oj-spotlight-img" style={{ backgroundImage: `url(${SHOT1})` }} />
                </div>
            </section>

            {/* ── Spotlight 2 ── */}
            <section className="oj-spotlight oj-spotlight--light">
                <div className="oj-container oj-spotlight-inner oj-spotlight-inner--reverse">
                    <div className="oj-spotlight-text">
                        <p className="oj-eyebrow">Tandem Management</p>
                        <h2 className="oj-spotlight-h2 oj-spotlight-h2--dark">Slot limits that actually work.</h2>
                        <p className="oj-spotlight-body oj-spotlight-body--dark">
                            Set your tandems-per-hour cap and let the system do the math.
                            Each instructor holds one tandem slot per load — OnJumprun enforces it
                            so you never overbook an instructor or a time slot again.
                        </p>
                        <a href="#contact" className="oj-btn-primary">Book a Walkthrough</a>
                    </div>
                    <div className="oj-spotlight-img" style={{ backgroundImage: `url(${SHOT2})` }} />
                </div>
            </section>

            {/* ── Comparison ── */}
            <section className="oj-compare" id="compare">
                <div className="oj-container">
                    <p className="oj-eyebrow">OnJumprun vs. Burble</p>
                    <h2 className="oj-section-h2 oj-section-h2--dark">Built for how DZs actually run.</h2>
                    <div className="oj-compare-table">
                        <div className="oj-compare-header">
                            <span />
                            <span className="oj-compare-col-label oj-compare-col-us">OnJumprun</span>
                            <span className="oj-compare-col-label">Burble</span>
                        </div>
                        {COMPARE.map(([label, us, them]) => (
                            <div className="oj-compare-row" key={label}>
                                <span className="oj-compare-label">{label}</span>
                                <span className={`oj-compare-check${us ? ' oj-check--yes' : ' oj-check--no'}`}>{us ? '✓' : '✗'}</span>
                                <span className={`oj-compare-check${them ? ' oj-check--yes' : ' oj-check--no'}`}>{them ? '✓' : '✗'}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── CTA ── */}
            <section className="oj-cta" id="contact">
                <div className="oj-container oj-cta-inner">
                    <h2 className="oj-cta-h2">Interested in OnJumprun?</h2>
                    <p className="oj-cta-sub">
                        The app is in active development. Reach out if you're a DZO who wants
                        to be involved early — feedback from operators is what's shaping it.
                    </p>
                    <a href="mailto:info@onjumprun.com" className="oj-btn-primary oj-btn-lg">
                        info@onjumprun.com
                    </a>
                </div>
            </section>

            {/* ── Footer ── */}
            <footer className="oj-footer">
                <span>© 2025 OnJumprun</span>
                <Link to="/" className="oj-back-link">Built by Roland Forbes</Link>
            </footer>
        </div>
    );
};

export default OnJumprun;
